import { useState, useEffect, useCallback, forwardRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, Download, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, ChevronsUpDown, X, FileText } from "lucide-react";
import ThemeChip from "./ThemeChip";
import { fmtCr } from "../utils/format";

const API = process.env.REACT_APP_BACKEND_URL;

const statusClass = (s) => {
  if (!s) return "bg-slate-100 text-slate-500";
  const l = s.toLowerCase();
  if (l === "ongoing") return "bg-green-50 text-green-700 border border-green-200";
  if (l === "closed") return "bg-slate-100 text-slate-600 border border-slate-200";
  return "bg-amber-50 text-amber-700 border border-amber-200";
};

const fmtDate = (d) => { if (!d) return "–"; try { return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); } catch { return d; } };

function SortTh({ label, field, sortBy, sortOrder, onSort, className = "" }) {
  const active = sortBy === field;
  return (
    <th className={`text-left font-medium text-slate-500 uppercase py-2 px-2 whitespace-nowrap cursor-pointer select-none hover:text-slate-800 hover:bg-slate-100 transition-colors group ${className}`}
      onClick={() => onSort(field)}>
      <span className="inline-flex items-center gap-1">
        {label}
        <span className={`transition-colors ${active ? "text-[#006837]" : "text-slate-500 group-hover:text-slate-600"}`}>
          {active ? (sortOrder === "asc" ? <ChevronUp size={12} /> : <ChevronDown size={12} />) : <ChevronsUpDown size={12} />}
        </span>
      </span>
    </th>
  );
}

const GrantsTableSection = forwardRef(({ filters = {}, extraTitle = "" }, ref) => {
  const navigate = useNavigate();
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("amount_recommended");
  const [sortOrder, setSortOrder] = useState("desc");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [exporting, setExporting] = useState(false);

  useEffect(() => { setPage(1); }, [filters.theme, filters.state, filters.fy, filters.view_mode]);

  useEffect(() => {
    setLoading(true);
    const params = { page, page_size: 50, sort_by: sortBy, sort_order: sortOrder, ...filters };
    if (search) params.search = search;
    axios.get(`${API}/api/grants/list`, { params })
      .then(res => {
        setGrants(res.data.data || []);
        setTotal(res.data.total || 0);
        setTotalPages(res.data.total_pages || 1);
      }).catch(console.error).finally(() => setLoading(false));
  }, [page, sortBy, sortOrder, search, filters]);

  const handleSort = useCallback((field) => {
    if (sortBy === field) { setSortOrder(o => o === "asc" ? "desc" : "asc"); }
    else { setSortBy(field); setSortOrder("desc"); }
    setPage(1);
  }, [sortBy]);

  const handleSearch = (e) => { e.preventDefault(); setSearch(searchInput); setPage(1); };
  const clearSearch = () => { setSearchInput(""); setSearch(""); setPage(1); };

  const handleExport = async () => {
    setExporting(true);
    try {
      const ep = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) ep.append(k, v); });
      const res = await axios.get(`${API}/api/grants/export?${ep}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = "grants.csv"; a.click();
    } catch (e) { console.error(e); } finally { setExporting(false); }
  };

  return (
    <div ref={ref} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="grants-table-section">
      <div className="px-5 pt-4 pb-3 border-b border-slate-100 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Grants{extraTitle ? ` — ${extraTitle}` : ""}</h3>
          <div className="flex items-center gap-1.5 mt-0.5">
            <p className="text-xs text-slate-500">{total.toLocaleString()} grants</p>
            <span className="text-[11px] text-[#006837] font-semibold">Click on Grant ID to view more details</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <form onSubmit={handleSearch} className="flex items-center gap-1.5">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-600" />
              <input type="text" value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search grants..." className="pl-8 pr-7 py-1.5 border border-slate-200 rounded-lg text-xs w-56 focus:outline-none focus:ring-2 focus:ring-[#006837]/20 focus:border-[#006837]"
                data-testid="section-grants-search" />
              {searchInput && (
                <button type="button" onClick={clearSearch} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-600 hover:text-slate-600"><X size={12} /></button>
              )}
            </div>
            <button type="submit" className="px-2.5 py-1.5 bg-[#006837] text-white rounded-lg text-xs font-medium hover:bg-[#8B1515]">Search</button>
          </form>
          <button onClick={handleExport} disabled={exporting}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-medium hover:bg-green-700 disabled:opacity-60 transition-colors whitespace-nowrap"
            data-testid="section-grants-export">
            <Download size={13} />{exporting ? "Exporting..." : "Export CSV"}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#006837]" /></div>
      ) : grants.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-48 text-slate-600"><FileText size={40} className="mb-2 opacity-40" /><p className="text-sm">No grants found</p></div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="section-grants-table">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50">
                <SortTh label="Grant ID" field="grant_id" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} className="px-3" />
                <SortTh label="Project Title" field="project_title" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                <SortTh label="Grantee" field="grantee_name" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                <SortTh label="Portfolio" field="theme" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                <SortTh label="Status" field="status" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                <SortTh label="Sanctioned" field="amount_recommended" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} className="text-right" />
                <SortTh label="Disbursed" field="disbursed" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} className="text-right" />
                <SortTh label="Start Date" field="project_start_date" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                <SortTh label="End Date" field="project_end_date" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                <SortTh label="FY" field="fy" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
              </tr>
            </thead>
            <tbody>
              {grants.map((g, i) => (
                <tr key={g.grant_id || i} className={`border-b border-slate-50 hover:bg-slate-50 transition-colors ${i % 2 === 0 ? "bg-white" : "bg-slate-50/30"}`}>
                  <td className="py-2 px-3 font-mono whitespace-nowrap">
                    <button onClick={() => navigate(`/grant-overview?grant=${g.grant_id}`)}
                      className="text-[#006837] hover:underline cursor-pointer font-medium">{g.grant_id || "–"}</button>
                  </td>
                  <td className="py-2 px-2 whitespace-nowrap"><span className="font-medium text-slate-800 max-w-[200px] truncate block" title={g.project_title}>{g.project_title || "–"}</span></td>
                  <td className="py-2 px-2 whitespace-nowrap"><span className="text-slate-700 max-w-[140px] truncate block" title={g.grantee}>{g.grantee || "–"}</span></td>
                  <td className="py-2 px-2 whitespace-nowrap">{g.theme ? <ThemeChip theme={g.theme} /> : "–"}</td>
                  <td className="py-2 px-2 whitespace-nowrap"><span className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${statusClass(g.status)}`}>{g.status || "–"}</span></td>
                  <td className="py-2 px-2 text-slate-500 whitespace-nowrap">{g.fy || "–"}</td>
                  <td className="py-2 px-2 text-right font-medium text-slate-800 whitespace-nowrap">{g.amount_cr ? fmtCr(g.amount_cr) : "–"}</td>
                  <td className="py-2 px-2 text-right font-medium text-green-700 whitespace-nowrap">{g.disbursed_cr ? fmtCr(g.disbursed_cr) : "–"}</td>
                  <td className="py-2 px-2 text-slate-500 whitespace-nowrap">{fmtDate(g.start_date)}</td>
                  <td className="py-2 px-2 text-slate-500 whitespace-nowrap">{fmtDate(g.end_date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-slate-50">
          <div className="text-xs text-slate-500">Showing {((page - 1) * 50) + 1}–{Math.min(page * 50, total)} of {total} grants</div>
          <div className="flex items-center gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="p-1.5 rounded border border-slate-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-100"><ChevronLeft size={14} /></button>
            <span className="text-xs text-slate-600">Page {page} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="p-1.5 rounded border border-slate-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-100"><ChevronRight size={14} /></button>
          </div>
        </div>
      )}
    </div>
  );
});

GrantsTableSection.displayName = "GrantsTableSection";
export default GrantsTableSection;
